import { ChannelFormField, ChannelProperties } from "../backend-types/channel";
import { BffSession, BffSessionType } from "../backend-types/session";
import {
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "preact/hooks";
import { useSdk, useSession } from "./session-provider";
import FieldGroup from "./field-group";
import { formHasFieldOfType, usePrevious } from "../utils";
import { createContext } from "preact";
import { forwardRef } from "preact/compat";
import { InternalSetFieldTouchedEvent } from "../private-event-types";
import { getChannelPropertyValue } from "../validation";
import { ChannelComponentData } from "../public-sdk";
import { CARDS_SCENARIOS, Scenarios } from "../data/simulation-scenarios";
import { useChannel, useChannelComponentData } from "./channel-root";

interface Props {
  form: ChannelFormField[];
  onChannelPropertiesChanged: (channelProperties: ChannelProperties) => void;
}
export interface ChannelFormHandle {
  setAllFieldsTouched: () => void;
}

const ChannelForm = forwardRef<ChannelFormHandle, Props>(
  ({ form, onChannelPropertiesChanged }, ref) => {
    const session = useSession();
    const channel = useChannel();
    const sdk = useSdk();
    const channelComponentData = useChannelComponentData();
    const formRef = useRef<HTMLFormElement>(null);

    const [channelProperties, setChannelProperties] =
      useState<ChannelProperties | null>(null);

    useImperativeHandle(ref, () => ({
      setAllFieldsTouched() {
        const form = formRef.current;
        if (!form) return;
        Array.from(form.elements)
          .filter((el) => el instanceof HTMLInputElement)
          .forEach((input) => {
            if (!input.name) {
              // only mark named fields as touched
              return;
            }
            input.dispatchEvent(new InternalSetFieldTouchedEvent());
          });
      },
    }));

    const getChannelProperties = useCallback((): ChannelProperties => {
      if (!formRef.current) return {};

      // The browser FormData collides with node's FormData, both are global, so we
      // need to make up a type for
      const formData = new FormData(formRef.current) as unknown as {
        entries: () => IterableIterator<[string, string | Blob]>;
      };

      return formKvToChannelProperties(formData.entries());
    }, []);

    const handleFieldChanged = useCallback(() => {
      if (!formRef.current) return;
      const channelProperties = getChannelProperties();
      setChannelProperties(channelProperties);
      onChannelPropertiesChanged(channelProperties);
    }, [getChannelProperties, onChannelPropertiesChanged]);

    // call onChannelPropertiesChanged once on init
    useLayoutEffect(() => {
      handleFieldChanged();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filteredForm = useFilteredFormFields(
      session,
      form,
      channelProperties || {},
      channelComponentData ?? null,
    );

    // trigger a field changed callback when the form changes
    const previousFilteredForm = usePrevious(filteredForm);
    useEffect(() => {
      if (
        // only trigger if the form changed
        !formsAreEqual(previousFilteredForm || [], filteredForm)
      ) {
        handleFieldChanged();
      }
    }, [filteredForm, handleFieldChanged, previousFilteredForm]);

    const getSimulationScenarios = useCallback(
      (fieldGroup: ChannelFormField[]): Scenarios | null => {
        // only show simulation scenarios for prod-dev (test mode)
        if (!sdk.supportsSimulationScenarios()) {
          return null;
        }

        // only show simulation scenarios for cards channel and if the field group has a credit card number field
        if (
          channel?.channel_code === "CARDS" &&
          fieldGroup.some((field) => field.type.name === "credit_card_number")
        ) {
          return CARDS_SCENARIOS;
        }

        return null;
      },
      [channel, sdk],
    );

    const filteredFieldGroups = groupFields(filteredForm).filter(
      (group) => group.length,
    );

    if (filteredFieldGroups.length === 0) {
      return null;
    }

    return (
      <div class="xendit-channel-form">
        <form ref={formRef}>
          <ChannelPropertiesContext.Provider value={channelProperties}>
            {filteredFieldGroups.map((fieldGroup, index) => (
              <FieldGroup
                key={index}
                fieldGroup={fieldGroup}
                groupIndex={index}
                handleFieldChanged={handleFieldChanged}
                channelProperties={channelProperties}
                simulationScenarios={getSimulationScenarios(fieldGroup)}
              />
            ))}
          </ChannelPropertiesContext.Provider>
        </form>
      </div>
    );
  },
);

export const ChannelPropertiesContext = createContext<ChannelProperties | null>(
  null,
);

export const useChannelProperties = (): ChannelProperties | null => {
  return useContext(ChannelPropertiesContext);
};

function groupFields(fields: ChannelFormField[]): ChannelFormField[][] {
  // Group fields for rendering
  const fieldGroups: ChannelFormField[][] = [[]];
  for (const field of fields) {
    if (
      field.span === 1 &&
      fieldGroups[fieldGroups.length - 1].length === 1 &&
      fieldGroups[fieldGroups.length - 1][0].span === 1
    ) {
      // join two half-width fields into one group
      fieldGroups[fieldGroups.length - 1].push(field);
      continue;
    }

    if (field.join) {
      // if the field should be explicitly joined, add it to the last group
      fieldGroups[fieldGroups.length - 1].push(field);
      continue;
    }

    // otherwise, start a new group
    fieldGroups.push([field]);
  }

  return fieldGroups;
}

/**
 * Convert form key/value pairs to channel properties
 * .e.g
 * Input:
 * {
 *   "k": "v1",
 *   "a.b.c": "v2",
 *   "z.z__a.y": ["v3", "v3"],
 * }
 * Output:
 * {
 *   k: "v1",
 *   a: { b: { c: "v2", }, },
 *   z: { z: "v3", y: "v4" },
 * }
 **/
function formKvToChannelProperties(
  iter: IterableIterator<[string, string | Blob]>,
): ChannelProperties {
  const obj: ChannelProperties = {};

  for (const [key, rawValue] of iter) {
    if (rawValue instanceof Blob) {
      continue;
    }

    // keys with __ represent multiple k/v pairs
    // e.g. `{"a__b": ["1", "2"]}` becomes `{a: "1", b: "2"}`
    const subkeys = key.split("__");

    // if there are multiple subkeys, assume the value is a JSON array of strings
    const valueAsArray = formValueToStringArray(subkeys, rawValue);

    outer: for (const subkey of subkeys) {
      // split key by dot, for each part, traverse the object
      // and assign the value at the end
      // e.g. { "branch.leaf": "value" } becomes { branch: { leaf: "value" } }
      // cursor will be the leaf object
      const parts = subkey.split(".");
      let cursor = obj;
      while (parts.length > 1) {
        const part = parts.shift()!;
        let selected = cursor[part];
        if (selected === undefined) {
          // child object doesn't exist, create it
          selected = cursor[part] = {};
        }
        if (selected && typeof selected === "object") {
          if (Array.isArray(selected)) {
            continue outer; // should never happen
          }
          // traverse into child object
          cursor = selected;
        }
      }

      // assign next value to channel properties
      const nextValue = valueAsArray.length ? valueAsArray.shift() : "";

      // wrap in array if the subkey ends in []
      if (parts[0].endsWith("[]")) {
        cursor[parts[0].slice(0, -2)] = [nextValue];
      } else {
        cursor[parts[0]] = nextValue;
      }
    }
  }

  return obj;
}

/**
 * Parse a json string[] with error handling.
 */
function formValueToStringArray(
  subkeys: string[],
  value: string,
): (string | number)[] {
  if (subkeys.length === 0) return [];
  if (subkeys.length === 1) return [value];
  if (value === "") return [];
  try {
    return JSON.parse(value);
  } catch (_e) {
    return [value];
  }
}

/**
 * Checks if two forms are equal
 */
function formsAreEqual(a: ChannelFormField[], b: ChannelFormField[]) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Takes a form and filters out fields that should not be shown based on context.
 */
export function useFilteredFormFields(
  session: BffSession,
  form: ChannelFormField[],
  channelProperties: ChannelProperties,
  channelComponentData: ChannelComponentData | null,
) {
  const filteredForm = useMemo(() => {
    return filterFormFields(
      session.session_type,
      form,
      channelProperties,
      channelComponentData,
    );
  }, [channelComponentData, channelProperties, form, session.session_type]);

  return filteredForm;
}

export function filterFormFields(
  sessionType: BffSessionType,
  form: ChannelFormField[],
  channelProperties: ChannelProperties,
  channelComponentData: ChannelComponentData | null,
) {
  const hasCardsField = formHasFieldOfType(form, "credit_card_number");
  const showBillingDetailsFields =
    channelComponentData?.cardDetails?.details?.require_billing_information;
  const hasInstallmentPlans =
    !!channelComponentData?.paymentOptions?.options?.installment_plans?.length;

  return form.filter((field) => {
    if (field.flags?.require_billing_information) {
      // filter out billing info fields if the flag is disabled or the session type is not PAY
      if (sessionType !== "PAY") return false;
      if (!showBillingDetailsFields) return false;
    }
    if (field.type.name === "installment_plan") {
      // filter out installment plan fields if there are no installment plans AND there is a card number field
      // (if there's no card number, then the installments field would be mandatory so we can't hide it)
      if (!hasInstallmentPlans && hasCardsField) return false;
    }
    for (const condition of field.display_if || []) {
      // filter out fields if any of their display_if conditions fail
      const [property, operator, value] = condition;
      const channelValue = getChannelPropertyValue(channelProperties, property);
      switch (operator) {
        case "equals":
          if (channelValue !== value) return false;
          break;
        case "not_equals":
          if (channelValue === value) return false;
          break;
      }
    }
    return true;
  });
}

export default ChannelForm;
